import { useEffect, useRef, useState } from "react";
import { FiArrowUpRight, FiCheck, FiEdit2, FiTag, FiTrash2, FiX } from "react-icons/fi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { Link } from "react-router-dom";
import useEditTag from "api/TagResource/useEditTag";
import TagDeleteDialog from "./TagDeleteDialog";

export default function TagRow({ tag, disabled = false }) {
	const [editing, setEditing] = useState(false);
	const [name, setName] = useState(tag.name);
	const [error, setError] = useState("");
	const [confirmDelete, setConfirmDelete] = useState(false);
	const input = useRef(null), rename = useRef(null);
	const { mutateAsync, isLoading } = useEditTag(tag.id);
	const count = Number(tag.books_count || 0), inUse = count > 0;
	const errorId = `tag-${tag.id}-error`;
	useEffect(() => { if (editing) { input.current?.focus(); input.current?.select(); } }, [editing]);
	useEffect(() => { if (!editing) setName(tag.name); }, [tag.name, editing]);
	const finish = () => { setEditing(false); setError(""); requestAnimationFrame(() => rename.current?.focus()); };
	const cancel = () => { if (isLoading) return; setName(tag.name); finish(); };
	const save = async (event) => {
		event.preventDefault();
		if (isLoading || disabled) return;
		const value = name.trim();
		if (!value) { setError("Enter a tag name."); input.current?.focus(); return; }
		if (value === tag.name) { finish(); return; }
		setError("");
		try { await mutateAsync({ name: value }); finish(); }
		catch { setError("This tag couldn’t be renamed. Check the name and try again."); }
	};
	return <li className={`tag-row${editing ? " is-editing" : ""}`}>
		{editing ? <form onSubmit={save} className="tag-rename-form" aria-busy={isLoading}>
			<div className="tag-rename-field"><label htmlFor={`tag-${tag.id}-name`}>Tag name</label>
				<input ref={input} id={`tag-${tag.id}-name`} value={name} disabled={isLoading || disabled} maxLength={60}
					aria-invalid={!!error} aria-describedby={error ? errorId : undefined} onChange={(event) => { setName(event.target.value); setError(""); }}
					onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); cancel(); } }} />
			</div>
			<div className="tag-rename-actions"><button type="submit" className="tag-save" disabled={isLoading || disabled}>
				{isLoading ? <AiOutlineLoading3Quarters className="animate-spin" aria-hidden="true" /> : <FiCheck aria-hidden="true" />}{isLoading ? "Saving…" : "Save"}</button>
				<button type="button" disabled={isLoading} onClick={cancel}><FiX aria-hidden="true" />Cancel</button></div>
			{error && <p id={errorId} className="tag-inline-error" role="alert">{error}</p>}
		</form> : <>
			<div className="tag-name-cell"><span className="tag-row-symbol" aria-hidden="true"><FiTag /></span>
				<button ref={rename} type="button" className="tag-name" onClick={() => setEditing(true)} disabled={disabled} aria-label={`Rename ${tag.name}`}>{tag.name}</button>
			</div>
			<div className="tag-usage-cell">{inUse ? <Link to={`/books/admin/all?tagIs=${encodeURIComponent(tag.name)}`} className="tag-book-link" aria-label={`View ${count} ${count === 1 ? "book" : "books"} tagged ${tag.name}`}>
				<span className="tag-status-dot" aria-hidden="true" />{count.toLocaleString()} {count === 1 ? "book" : "books"}<FiArrowUpRight aria-hidden="true" />
			</Link> : <span className="tag-unused"><span className="tag-status-dot" aria-hidden="true" />Not used yet</span>}</div>
			<div className="tag-row-actions"><button type="button" className="tag-rename-trigger" onClick={() => setEditing(true)} disabled={disabled} aria-label={`Rename tag ${tag.name}`}><FiEdit2 aria-hidden="true" /><span>Rename</span></button>
				<button type="button" className="tag-delete-trigger" onClick={() => setConfirmDelete(true)} disabled={inUse || disabled} aria-label={`Delete ${tag.name}`} title={inUse ? "Tags linked to books cannot be deleted" : `Delete ${tag.name}`}><FiTrash2 aria-hidden="true" /></button></div>
		</>}
		<TagDeleteDialog tag={tag} visible={confirmDelete} onHide={() => setConfirmDelete(false)} />
	</li>;
}
